import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/layout/AppLayout";
import { Loading } from "@/components/common/ui-bits";
import { Login, ForgotPassword, ResetPassword, homeFor } from "@/pages/AuthPages";
import Dashboard from "@/pages/Dashboard";
import Leads from "@/pages/Leads";
import LeadDetail from "@/pages/LeadDetail";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import Services from "@/pages/Services";
import TaxCases from "@/pages/TaxCases";
import TaxCaseDetail from "@/pages/TaxCaseDetail";
import Quotes from "@/pages/Quotes";
import QuoteEditor from "@/pages/QuoteEditor";
import Proposals from "@/pages/Proposals";
import ProposalEditor from "@/pages/ProposalEditor";
import ProposalDetail from "@/pages/ProposalDetail";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import Tasks from "@/pages/Tasks";
import Support from "@/pages/Support";
import Templates from "@/pages/Templates";
import Users from "@/pages/Users";
import Audit from "@/pages/Audit";
import Profile from "@/pages/Profile";
import Help from "@/pages/Help";
import Settings from "@/pages/Settings";
import Invoices from "@/pages/Invoices";
import InvoiceEditor from "@/pages/InvoiceEditor";
import InvoiceDetail from "@/pages/InvoiceDetail";
import Payments from "@/pages/Payments";
import Renewals from "@/pages/Renewals";
import RenewalDetail from "@/pages/RenewalDetail";
import Notifications from "@/pages/Notifications";
import Reports from "@/pages/Reports";
import DeliveryDoc from "@/pages/DeliveryDoc";
import Unsubscribe from "@/pages/Unsubscribe";
import DocView from "@/pages/DocView";
import WebIntake from "@/pages/WebIntake";
import WebApprove from "@/pages/WebApprove";
import Plans from "@/pages/Plans";
import PortalPreferences from "@/pages/portal/PortalPreferences";
import PortalHome from "@/pages/portal/PortalHome";
import PortalProposal from "@/pages/portal/PortalProposal";
import { PortalProposals, PortalProjects, PortalTaxes, PortalDocuments } from "@/pages/portal/PortalLists";

function StaffOnPortal() {
  const { logout } = useAuth();
  const loc = useLocation();
  const next = loc.pathname + loc.search;
  const m = loc.pathname.match(/^\/portal\/propuestas\/([^/]+)/);
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6" data-testid="staff-on-portal">
      <div className="card-box max-w-md p-6 text-center">
        <h1 className="font-display text-xl font-bold text-slate-900">Este enlace es para el portal del cliente</h1>
        <p className="mt-2 text-sm text-slate-600">Usted tiene abierta una sesión del equipo interno. Cierre sesión y entre con la cuenta del cliente para verlo como él.</p>
        <div className="mt-5 flex flex-col gap-2">
          <button className="rounded-md bg-blue-700 px-4 py-2 font-semibold text-white hover:bg-blue-800" data-testid="staff-portal-logout"
            onClick={async () => { await logout(); window.location.assign(`/login?next=${encodeURIComponent(next)}`); }}>Cerrar sesión y entrar como cliente</button>
          <Link to={m ? `/propuestas/${m[1]}` : "/"} className="rounded-md border px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100" data-testid="staff-portal-admin-view">{m ? "Ver la propuesta como administrador" : "Ir al panel"}</Link>
        </div>
      </div>
    </div>
  );
}

function Guard({ roles, children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  if (!roles.includes(user.role)) {
    if (roles.includes("client") && loc.pathname !== "/portal") return <StaffOnPortal />;
    return <Navigate to={user.role === "client" ? "/portal" : "/"} replace />;
  }
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <Loading />;
  if (user) return <Navigate to={homeFor(user, new URLSearchParams(loc.search).get("next"))} replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/recuperar-contrasena" element={<ForgotPassword />} />
      <Route path="/restablecer-contrasena" element={<ResetPassword />} />
      <Route element={<Guard roles={["admin", "staff"]}><AppLayout /></Guard>}>
        <Route index element={<Dashboard />} />
        <Route path="prospectos" element={<Leads />} />
        <Route path="prospectos/:id" element={<LeadDetail />} />
        <Route path="clientes" element={<Clients />} />
        <Route path="clientes/:id" element={<ClientDetail />} />
        <Route path="servicios" element={<Services />} />
        <Route path="taxes" element={<TaxCases />} />
        <Route path="taxes/:id" element={<TaxCaseDetail />} />
        <Route path="cotizaciones" element={<Quotes />} />
        <Route path="cotizaciones/nueva" element={<QuoteEditor />} />
        <Route path="cotizaciones/:id" element={<QuoteEditor />} />
        <Route path="propuestas" element={<Proposals />} />
        <Route path="propuestas/nueva" element={<ProposalEditor />} />
        <Route path="propuestas/:id" element={<ProposalDetail />} />
        <Route path="propuestas/:id/editar" element={<ProposalEditor />} />
        <Route path="proyectos" element={<Projects />} />
        <Route path="proyectos/:id" element={<ProjectDetail />} />
        <Route path="tareas" element={<Tasks />} />
        <Route path="solicitudes" element={<Support />} />
        <Route path="solicitudes/:id" element={<Support />} />
        <Route path="facturas" element={<Invoices />} />
        <Route path="facturas/nueva" element={<Guard roles={["admin"]}><InvoiceEditor /></Guard>} />
        <Route path="facturas/:id" element={<InvoiceDetail />} />
        <Route path="facturas/:id/editar" element={<Guard roles={["admin"]}><InvoiceEditor /></Guard>} />
        <Route path="pagos" element={<Payments />} />
        <Route path="renovaciones" element={<Renewals />} />
        <Route path="renovaciones/:id" element={<RenewalDetail />} />
        <Route path="correos" element={<Guard roles={["admin"]}><Notifications /></Guard>} />
        <Route path="reportes" element={<Guard roles={["admin"]}><Reports /></Guard>} />
        <Route path="documentacion" element={<Guard roles={["admin"]}><DeliveryDoc /></Guard>} />
        <Route path="plantillas" element={<Guard roles={["admin"]}><Templates /></Guard>} />
        <Route path="usuarios" element={<Guard roles={["admin"]}><Users /></Guard>} />
        <Route path="actividad" element={<Guard roles={["admin"]}><Audit /></Guard>} />
        <Route path="ayuda" element={<Guard roles={["admin"]}><Help /></Guard>} />
        <Route path="configuracion" element={<Guard roles={["admin"]}><Settings /></Guard>} />
        <Route path="perfil" element={<Profile />} />
      </Route>
      <Route path="/portal" element={<Guard roles={["client"]}><AppLayout portal /></Guard>}>
        <Route index element={<PortalHome />} />
        <Route path="propuestas" element={<PortalProposals />} />
        <Route path="propuestas/:id" element={<PortalProposal />} />
        <Route path="proyectos" element={<PortalProjects />} />
        <Route path="proyectos/:id" element={<ProjectDetail />} />
        <Route path="taxes" element={<PortalTaxes />} />
        <Route path="taxes/:id" element={<TaxCaseDetail />} />
        <Route path="documentos" element={<PortalDocuments />} />
        <Route path="solicitudes" element={<Support />} />
        <Route path="facturas" element={<Invoices />} />
        <Route path="facturas/:id" element={<InvoiceDetail />} />
        <Route path="pagos" element={<Payments />} />
        <Route path="renovaciones" element={<Renewals />} />
        <Route path="renovaciones/:id" element={<RenewalDetail />} />
        <Route path="preferencias" element={<PortalPreferences />} />
        <Route path="perfil" element={<Profile />} />
      </Route>
      <Route path="/baja/:token" element={<Unsubscribe />} />
      <Route path="/d/:token" element={<DocView />} />
      <Route path="/cuestionario/:token" element={<WebIntake />} />
      <Route path="/aprobar/:token" element={<WebApprove />} />
      <Route path="/planes" element={<Plans />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </div>
  );
}
